import type {
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
} from "@tanstack/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { AxiosRequestConfig } from "axios";
import { apiRequest } from "./http";
import { ApiError } from "./types";

type UseApiQueryOptions<TData, TError, TQueryKey extends QueryKey> = Omit<
  UseQueryOptions<TData, TError, TData, TQueryKey>,
  "queryKey" | "queryFn"
>;

export const useApiQuery = <
  TData = unknown,
  TError = ApiError,
  TQueryKey extends QueryKey = QueryKey,
>(
  queryKey: TQueryKey,
  request: AxiosRequestConfig,
  options?: UseApiQueryOptions<TData, TError, TQueryKey>
) =>
  useQuery({
    queryKey,
    queryFn: () => apiRequest<TData>(request),
    ...options,
  });

type UseApiMutationOptions<TData, TError, TVariables, TContext> = Omit<
  UseMutationOptions<TData, TError, TVariables, TContext>,
  "mutationFn"
>;

type RequestFactory<TVariables> = (
  variables: TVariables
) => AxiosRequestConfig<TVariables>;

export const useApiMutation = <
  TData = unknown,
  TVariables = void,
  TError = ApiError,
  TContext = unknown,
>(
  factory: RequestFactory<TVariables>,
  options?: UseApiMutationOptions<TData, TError, TVariables, TContext>
) =>
  useMutation<TData, TError, TVariables, TContext>({
    mutationFn: async (variables) => {
      const config = factory(variables);

      if (!config.method || config.method.toLowerCase() !== "get") {
        if (config.data === undefined) {
          config.data = variables;
        }
      }

      return apiRequest<TData>(config);
    },
    ...options,
  });
